/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useEffect } from "react";

import * as React from "react";
import { AppHeader } from "components/shared/AppHeader";
import { SpectrumCssWrapper } from "components/shared/SpectrumCss";
import { ScrollToTop } from "components/shared/ScrollToTop";
import { withSpectrumReact } from "components/shared/WithSpectrumReact";
import { FreeTierEnhanceSpeech } from "components/EnhancedSpeech/FreeTierEnhanceSpeech";
import { useCurrentUser } from "actions/CurrentUserActions";
import { ProTierEnhanceSpeech } from "components/EnhancedSpeech/ProTierEnhanceSpeech";
import { Analytics, AnalyticsEvent } from "constants/Analytics";
import { withComponentAsNewRelicRoot } from "hooks/withComponentAsNewRelicRoot";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import featuresBackdropImg from "components/shared/images/features-backdrop.jpg";
import { devices, fontFamilies } from "components/shared/Styles";

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 32px;
  width: 100%;
`;

const EnhancedSpeechV2Banner = styled.div`
  display: flex;
  padding: 30px 0 82px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 79px;
  margin-bottom: -61px;
  gap: 4px;
  background: url("${featuresBackdropImg}") transparent 0px 1.032px / 100%
    105.852% no-repeat;
  width: 100%;
`;

const EnhancedSpeechV2BannerTitle = styled.h1`
  text-align: center;
  text-shadow: 0px 0px 36px #fff;
  font-family: ${fontFamilies};
  font-size: 40px;
  font-style: normal;
  font-weight: 900;
  line-height: 130%;
  max-width: 100%;
  background: linear-gradient(133deg, #5266ff 5.68%, #a16fff 96.33%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  ${devices.small} {
    max-width: 285px;
  }
`;

const EnhancedSpeechV2BannerSubtitle = styled.h2`
  color: var(--spectrum-global-color-gray-900);
  text-align: center;
  font-family: ${fontFamilies};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%;
  max-width: 100%;
  ${devices.small} {
    max-width: 285px;
  }
`;

/*
 We use signed urls with expirations for the files users upload + their
 enhanced versions. Reloading the page like this lets us assume that all urls
 are always valid, since the refresh timeout is lower than the expiration
 timeout.
 */
const useTimeoutRefresh = (): void => {
  useEffect(() => {
    const EIGHT_HOURS = 1000 * 60 * 60 * 8;
    setTimeout(() => {
      window.location.reload();
    }, EIGHT_HOURS);
  }, []);
};

const EnhancedSpeechHeading: React.FC = () => {
  return (
    <EnhancedSpeechV2Banner>
      <EnhancedSpeechV2BannerTitle>
        {I18n.t("enhance_speech.v2_banner.title")}
      </EnhancedSpeechV2BannerTitle>
      <EnhancedSpeechV2BannerSubtitle>
        {I18n.t("enhance_speech.v2_banner.subtitle")}
      </EnhancedSpeechV2BannerSubtitle>
    </EnhancedSpeechV2Banner>
  );
};

const EnhancedSpeech: React.FC = () => {
  const { currentUser } = useCurrentUser();

  useTimeoutRefresh();

  useEffect(() => {
    if (currentUser) {
      Analytics.instance.logEvent(
        currentUser?.hasPremiumAccess
          ? AnalyticsEvent.USER_VISIT_PREMIUM_ENHANCE
          : AnalyticsEvent.USER_VISIT_FREE_ENHANCE
      );
    }
  }, [currentUser]);

  // AppRouter usually manages the NewRelicRoot but since EnhancedSpeech forks
  // which experience the user gets here, the NewRelicRoot needs to be managed
  // here. In general, we shouldn't call `withComponentAsNewRelicRoot` any lower
  // than AppRouter though.
  const Component = withComponentAsNewRelicRoot(
    currentUser?.hasPremiumAccess ? ProTierEnhanceSpeech : FreeTierEnhanceSpeech
  );

  return (
    <SpectrumCssWrapper>
      <Helmet>
        <html lang={I18n.locale} />
        <title>{I18n.t("enhance_speech.meta.title")}</title>
        <meta
          name="description"
          content={I18n.t("enhance_speech.meta.description")}
        />
        <meta
          property="og:title"
          content={I18n.t("enhance_speech.meta.title")}
        />
      </Helmet>
      <ScrollToTop />
      <AppHeader />
      <PageWrapper>
        <EnhancedSpeechHeading />
        <Component />
      </PageWrapper>
    </SpectrumCssWrapper>
  );
};

export default withSpectrumReact(EnhancedSpeech);
